import { Autocomplete, AutocompleteItem, Input, Select, SelectItem, Tooltip } from "@nextui-org/react";
import { useTranslation } from 'react-i18next';
import { useMemo, useEffect, useState } from 'react';
import { CtyButton, useScreenWidth } from "@circuitry-ai/doc-ui";
import { useForm, Controller } from 'react-hook-form';
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import { RootState, useGetTenantTimezonesQuery } from "@circuitry-ai/doc-data";

export interface UserFormDetails {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    defaultLanguage: string;
    timezone: string;
    dateFormat: string;
    dateTimeFormat: string;
    skillSet: string;
    accessLevel?: string;
    email: string;
    userRole: string[];
}

interface UserDetailsProps {
    userProfileData: any;
    handleUserRoleUpdate: (roles: string[]) => void;
    submitButtonText: string;
    isSavingUserDetails: boolean;
    updateUserDetails: (userDetails: UserFormDetails) => void;
    isUpdatingUserRole: boolean;
    viewUserRole?: boolean;
    isSSOUser?: boolean;
}

const UserDetailsForm = (props: UserDetailsProps) => {

    const {
        userProfileData,
        handleUserRoleUpdate,
        submitButtonText,
        isSavingUserDetails,
        updateUserDetails,
        isUpdatingUserRole,
        viewUserRole = true,
        isSSOUser = false
    } = props;

    const { t } = useTranslation();
    const { details: user } = useSelector((state: RootState) => state.auth.user);

    const width = useScreenWidth();
    const isMobile = width <= 768;
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { data: timezoneOptions = [], isLoading: isTimezonesLoading } = useGetTenantTimezonesQuery();

    const skillSetOptions = useMemo(() => ['New User', 'Skilled', 'Master'], []);
    const userRoleOptions = useMemo(() => ['admin', 'editor', 'viewer'], []);
    const languageOptions = useMemo(() => [{ key: 'en-US', value: 'English' }, { key: 'te-IN', value: 'Telugu' }, { key: 'es-ES', value: 'Spanish' }], []);
    const dateFormatOptions = useMemo(
        () => ['', 'MM-dd-yyyy', 'dd-MM-yyyy', 'yyyy-MM-dd', 'MM/dd/yyyy', 'MMM-dd-yyyy', 'dd-MMM-yyyy'],
        []
    );
    const dateTimeFormatOptions = useMemo(
        () => ['', 'MM-dd-yyyy HH:mm:ss', 'dd-MM-yyyy HH:mm:ss', 'yyyy-MM-dd HH:mm:ss', 'MM/dd/yyyy HH:mm:ss', 'dd-MMM-yyyy HH:mm:ss', 'MMM-dd-yyyy HH:mm:ss'],
        []
    );
    const connectionDisplay = useMemo(() => {
        const list = userProfileData?.identities;
        if (!Array.isArray(list) || list.length === 0) return '';
        return list[0]?.connection ?? '';
    }, [userProfileData]);

    const defaultFormValues = useMemo<UserFormDetails>(() => ({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        defaultLanguage: '',
        timezone: '',
        dateFormat: '',
        dateTimeFormat: '',
        skillSet: '',
        userRole: ['viewer']
    }), []);

    const { control, getValues, reset, watch, formState: { errors, isValid, isDirty }, trigger } = useForm<UserFormDetails>({
        mode: "onChange",
        defaultValues: defaultFormValues
    });

    const hasAccessLevel = !!userProfileData?.user_metadata?.access_level;

    const loadedValues = useMemo<UserFormDetails | null>(() => {
        if (!userProfileData) return null;
        const roles = userProfileData?.roles;
        return {
            firstName: userProfileData?.given_name ?? userProfileData?.name ?? '',
            lastName: userProfileData?.family_name ?? '',
            email: userProfileData?.email ?? '',
            phoneNumber: userProfileData?.user_metadata?.phone_number ?? '',
            defaultLanguage: userProfileData?.user_metadata?.language ?? '',
            timezone: userProfileData?.user_metadata?.timezone ?? '',
            dateFormat: userProfileData?.user_metadata?.preference?.dateFormat ?? '',
            dateTimeFormat: userProfileData?.user_metadata?.preference?.dateTimeFormat ?? '',
            skillSet: userProfileData?.user_metadata?.skill ?? '',
            ...(userProfileData?.user_metadata?.access_level != null && { accessLevel: userProfileData.user_metadata.access_level }),
            userRole: Array.isArray(roles) ? roles : (typeof roles === 'string' ? [roles] : ['viewer'])
        };
    }, [userProfileData]);

    useEffect(() => {
        if (loadedValues) {
            reset(loadedValues);
        }
    }, [loadedValues, reset]);

    const onSubmit = async () => {
        setIsSubmitted(true);
        const isValidForm = await trigger();

        if (isValidForm) {
            const data = getValues();
            updateUserDetails(data);
        }
    };

    const handleCancel = () => {
        if (loadedValues) {
            reset(loadedValues);
        }
        setIsSubmitted(false);
    };

    const isUserRoleUpdateEnabled = userProfileData?.user_id !== user?.sub;
    const timezoneValue = watch('timezone');
    const mergedTimezoneOptions = useMemo(() => {
        if (!timezoneValue) return timezoneOptions;
        const hasTimezone = timezoneOptions.some((option) => option.name === timezoneValue);
        return hasTimezone ? timezoneOptions : [{ name: timezoneValue, abbrev: '' }, ...timezoneOptions];
    }, [timezoneOptions, timezoneValue]);

    return (
        <div className="flex flex-1 flex-col">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                <Controller
                    name="firstName"
                    control={control}
                    rules={{
                        required: "First Name is required",
                        minLength: { value: 2, message: "First Name must be at least 2 characters" },
                        maxLength: { value: 15, message: "First Name cannot exceed 15 characters" },
                        pattern: { value: /^[A-Za-z\s-]+$/, message: "First Name can only contain letters, spaces and hyphens" }
                    }}
                    render={({ field }) => (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">
                                {t('First Name')}<span className="text-danger">*</span>
                            </label>
                            <Input {...field} size="md" radius="md" isDisabled={isSSOUser} classNames={{ inputWrapper: 'border border-default-200 bg-content1' }} />
                            {errors.firstName && <p className="text-danger text-xs">{errors.firstName.message}</p>}
                        </div>
                    )}
                />
                <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: "Last Name is required" }}
                    render={({ field }) => (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">
                                {t('Last Name')}<span className="text-danger">*</span>
                            </label>
                            <Input {...field} size="md" radius="md" isDisabled={isSSOUser} classNames={{ inputWrapper: 'border border-default-200 bg-content1' }} />
                            {errors.lastName && <p className="text-danger text-xs">{errors.lastName.message}</p>}
                        </div>
                    )}
                />
                <Controller
                    name="email"
                    control={control}
                    rules={{ required: "Email is required" }}
                    render={({ field }) => (
                        <div className="md:col-span-2">
                            <label className="flex items-center gap-1.5 text-sm pb-1 font-medium text-foreground">
                                {t('Email')}<span className="text-danger">*</span>
                                <Tooltip content={t('Email cannot be changed here')} placement="top" classNames={{ content: 'text-sm' }}>
                                    <span className="inline-flex cursor-help text-default-400" aria-hidden>
                                        <Icon icon="solar:info-circle-linear" width="1rem" height="1rem" />
                                    </span>
                                </Tooltip>
                            </label>
                            <Input {...field} type="email" size="sm" isDisabled classNames={{ inputWrapper: 'border border-default-200 bg-content1', input: 'bg-content1' }} startContent={<Icon icon="mdi-light:email" width="1.2rem" height="1.2rem" className="text-default-500" />} />
                            {errors.email && <p className="text-danger text-xs mt-0.5">{errors.email.message}</p>}
                        </div>
                    )}
                />
            </div>
            <div className="md:col-span-2">
                <label className="flex items-center gap-1.5 text-sm pb-1 font-medium text-foreground">
                    {t('Connection')}<span className="text-danger">*</span>
                </label>
                <Input value={connectionDisplay} isReadOnly isDisabled size="sm" classNames={{ inputWrapper: 'border border-default-200 bg-content1', input: 'bg-content1' }} aria-label={t('Connection')} />
            </div>
            <div className="flex flex-col gap-6 mt-1">
                <Controller
                    name="phoneNumber"
                    control={control}
                    render={({ field }) => (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">
                                {t('Phone Number')}
                            </label>
                            <Input {...field} type="tel" size="md" radius="md" classNames={{ inputWrapper: 'border border-default-200 bg-content1' }} />
                            {errors.phoneNumber && <p className="text-danger text-xs">{errors.phoneNumber.message}</p>}
                        </div>
                    )}
                />
                <Controller
                    name="defaultLanguage"
                    control={control}
                    render={({ field }) => (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">
                                {t('Default Language')}
                            </label>
                            <Select
                                {...field}
                                id="user-default-language"
                                aria-label="user-default-language"
                                selectedKeys={field.value ? new Set([field.value]) : new Set([])}
                                size="md"
                                radius="md"
                                classNames={{ trigger: 'border border-default-200 bg-content1' }}
                                placeholder={t('Select a language')}
                                onSelectionChange={(keys) => {
                                    const selectedKey = Array.from(keys as Set<React.Key>)[0];
                                    field.onChange(selectedKey ? String(selectedKey) : '');
                                }}
                            >
                                {languageOptions.map((language) => (
                                    <SelectItem key={language.key} value={language.key}>{language.value}</SelectItem>
                                ))}
                            </Select>
                            {errors.defaultLanguage && <p className="text-danger text-xs">{errors.defaultLanguage.message}</p>}
                        </div>
                    )}
                />
                <Controller
                    name="skillSet"
                    control={control}
                    render={({ field }) => (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">
                                {t('Skill Set')}
                            </label>
                            <Select
                                id="user-skill-set"
                                aria-label="user-skill-set"
                                {...field}
                                selectedKeys={field.value ? new Set([field.value]) : new Set([])}
                                size="md"
                                radius="md"
                                classNames={{ trigger: 'border border-default-200 bg-content1' }}
                                placeholder={t('Select a skill set')}
                                onSelectionChange={(keys) => {
                                    const selectedKey = Array.from(keys as Set<React.Key>)[0];
                                    field.onChange(selectedKey ? String(selectedKey) : '');
                                }}
                            >
                                {skillSetOptions.map((skill) => (
                                    <SelectItem key={skill} value={skill}>{skill}</SelectItem>
                                ))}
                            </Select>
                            {errors.skillSet && <p className="text-danger text-xs">{errors.skillSet.message}</p>}
                        </div>
                    )}
                />
                <Controller
                    name="dateFormat"
                    control={control}
                    render={({ field }) => (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">
                                {t('Date Format')}
                            </label>
                            <Select
                                id="user-date-format"
                                aria-label="user-date-format"
                                selectedKeys={field.value ? new Set([field.value]) : new Set(['__empty__'])}
                                size="md"
                                radius="md"
                                classNames={{ trigger: 'border border-default-200 bg-content1' }}
                                placeholder={t('Select date format')}
                                onSelectionChange={(keys) => {
                                    const selectedKey = Array.from(keys as Set<React.Key>)[0];
                                    field.onChange(selectedKey === '__empty__' ? '' : String(selectedKey || ''));
                                }}
                            >
                                {dateFormatOptions.map((format) => (
                                    <SelectItem key={format || '__empty__'} value={format}>
                                        {format || ' '}
                                    </SelectItem>
                                ))}
                            </Select>
                        </div>
                    )}
                />
                <Controller
                    name="dateTimeFormat"
                    control={control}
                    render={({ field }) => (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">
                                {t('Date Time Format')}
                            </label>
                            <Select
                                id="user-date-time-format"
                                aria-label="user-date-time-format"
                                selectedKeys={field.value ? new Set([field.value]) : new Set(['__empty__'])}
                                size="md"
                                radius="md"
                                classNames={{ trigger: 'border border-default-200 bg-content1' }}
                                placeholder={t('Select date time format')}
                                onSelectionChange={(keys) => {
                                    const selectedKey = Array.from(keys as Set<React.Key>)[0];
                                    field.onChange(selectedKey === '__empty__' ? '' : String(selectedKey || ''));
                                }}
                            >
                                {dateTimeFormatOptions.map((format) => (
                                    <SelectItem key={format || '__empty__'} value={format}>
                                        {format || ' '}
                                    </SelectItem>
                                ))}
                            </Select>
                        </div>
                    )}
                />
                <Controller
                    name="timezone"
                    control={control}
                    render={({ field }) => (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">
                                {t('Time Zone')}
                            </label>
                            <Autocomplete
                                id="user-timezone"
                                aria-label="user-timezone"
                                size="md"
                                variant="bordered"
                                classNames={{ base: 'max-w-full' }}
                                placeholder={isTimezonesLoading ? t('Loading timezones...') : t('Select time zone')}
                                selectedKey={field.value || null}
                                onSelectionChange={(key) => field.onChange(key ? String(key) : '')}
                                isLoading={isTimezonesLoading}
                            >
                                {mergedTimezoneOptions.map((timezone) => (
                                    <AutocompleteItem key={timezone.name} textValue={`${timezone.name} (${timezone.abbrev || 'GMT'})`}>
                                        {`${timezone.name} (${timezone.abbrev || 'GMT'})`}
                                    </AutocompleteItem>
                                ))}
                            </Autocomplete>
                        </div>
                    )}
                />
                {hasAccessLevel && (
                    <Controller
                        name="accessLevel"
                        control={control}
                        render={({ field }) => (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-foreground">
                                    {t('Access Level')}
                                </label>
                                <Input {...field} value={field.value ?? ''} size="md" radius="md" classNames={{ inputWrapper: 'border border-default-200 bg-content1' }} />
                            </div>
                        )}
                    />
                )}
            </div>
            <footer className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-8 pt-6 border-t border-default-200">
                <div className="min-h-[2rem] flex items-center">
                    {isDirty && (
                        <span className="text-sm text-default-500">{t('You have unsaved changes')}</span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <CtyButton variant="bordered" onPress={handleCancel} isDisabled={!isDirty} className="min-w-[6rem]">
                        {t('Cancel')}
                    </CtyButton>
                    <CtyButton color="primary" onPress={onSubmit} isLoading={isSavingUserDetails} className="min-w-[8rem]">
                        {t('Save Changes')}
                    </CtyButton>
                </div>
            </footer>
            {viewUserRole && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:4 md:3 lg:3 my-3">
                <Controller
                    name="userRole"
                    control={control}
                    render={({ field }) => {
                        let selectedKeys;
                        if (Array.isArray(field.value)) {
                            selectedKeys = [...field.value];
                        } else {
                            selectedKeys = (field.value as string).split(',');
                        }
                        return (
                            <div className="">
                                <div >
                                    <div className="flex flex-row align-center gap-2 pb-1">
                                        <label className="flex items-center text-sm font-medium">
                                            {t('Role')}<span className="text-danger">*</span>
                                        </label>
                                    </div>
                                    <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-2`}>
                                        <Select
                                            {...field}
                                            size="sm"
                                            disallowEmptySelection
                                            selectedKeys={selectedKeys}
                                            isDisabled={!isUserRoleUpdateEnabled}
                                            placeholder="Select a user role"
                                            id="user-role"
                                            selectionMode="multiple"
                                            aria-label="user-role"
                                        >
                                            {userRoleOptions.map((role) => (
                                                <SelectItem key={role} value={role}>{role}</SelectItem>
                                            ))}
                                        </Select>
                                        {
                                            isUserRoleUpdateEnabled &&
                                            <CtyButton color="primary" onPress={() => handleUserRoleUpdate(Array.isArray(selectedKeys) ? selectedKeys : [...selectedKeys])} isLoading={isUpdatingUserRole}>
                                                {t(submitButtonText)}
                                            </CtyButton>
                                        }
                                    </div>

                                </div>

                            </div>
                        );
                    }}
                />
            </div>}
            {isSubmitted && !isValid && (
                <div className="mt-4 p-4 bg-danger/10 border border-danger rounded-md">
                    <h4 className="text-danger font-medium mb-2">{t('Please fix the following errors:')}</h4>
                    <ul className="text-danger text-sm space-y-1">
                        {errors.firstName && <li>• {errors.firstName.message}</li>}
                        {errors.lastName && <li>• {errors.lastName.message}</li>}
                        {errors.email && <li>• {errors.email.message}</li>}
                        {errors.phoneNumber && <li>• {errors.phoneNumber.message}</li>}
                        {errors.defaultLanguage && <li>• {errors.defaultLanguage.message}</li>}
                        {errors.skillSet && <li>• {errors.skillSet.message}</li>}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default UserDetailsForm;